import { cn } from "@RHCommerceDev/utils/cn";
import React from "react";
import { Typography } from "@RHCommerceDev/unstyled-ui-components";
import memoize from "@RHCommerceDev/utils/memoize";
import he from "he";
import { groupChildCategories } from "@RHCommerceDev/utils/parseCatalogNavigationData";
import { AnimatePresence, motion } from "motion/react";
import { CATALOG_CATEGORIES } from "@RHCommerceDev/utils/constants";

interface NavigationDropdownProps {
  isConcierge: boolean;
  selectedCategory: NavigationElement;
  groupedCategories: ReturnType<typeof groupChildCategories>;
  selectedPrimaryIndex: number;
  onPrimaryColumnButtonClick: (primaryIndex: number) => void;
  onPrimaryColumnLinkClick: (
    event: React.MouseEvent,
    primaryItem: NavigationElement
  ) => void;
  onSecondaryColumnLinkClick: (
    event: React.MouseEvent,
    secondaryItem: NavigationElement,
    groupHeader?: NavigationElement | null
  ) => void;
  waterWorksUrl?: string;
  dropdownHeight?: string;
  selectedCategoryIndex: number;
  closeCategoryDropdown: () => void;
}

/*
 * This is a presentation component responsible solely for rendering the navigation dropdown UI.It should not contain any business logic.
 */

const NavigationDropdown = ({
  isConcierge,
  selectedCategory,
  groupedCategories,
  selectedPrimaryIndex,
  onPrimaryColumnButtonClick,
  onPrimaryColumnLinkClick,
  onSecondaryColumnLinkClick,
  waterWorksUrl,
  dropdownHeight,
  selectedCategoryIndex,
  closeCategoryDropdown
}: NavigationDropdownProps) => (
  <AnimatePresence mode="wait">
    {selectedCategoryIndex !== -1 && (
      <motion.div
        key="dropdown"
        initial={{ y: "-100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: "-100%", opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed inset-0 z-[1099] flex h-full w-full flex-col justify-end bg-rh-mocha text-white"
        onClick={() => {
          closeCategoryDropdown();
        }}
      >
        <div
          className="flex gap-20 px-10 pt-10 xl:px-20"
          style={{
            height: dropdownHeight
          }}
        >
          {/* PRIMARY COLUMN */}
          <motion.div
            key={selectedCategory?.id}
            className="flex flex-col items-start gap-4 overflow-auto [-ms-overflow-style:none] [scrollbar-width:none] lg:gap-5 [&::-webkit-scrollbar]:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 0.3, duration: 0.3 } }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            onClick={e => {
              //this stop dropdown to close when clicked
              e.stopPropagation();
            }}
          >
            {selectedCategory?.childCategories?.map(
              (primaryItem, primaryIndex) => (
                <button
                  key={primaryIndex}
                  onClick={e => {
                    if (!!primaryItem?.childCategories?.length) {
                      onPrimaryColumnButtonClick(primaryIndex);
                    } else {
                      onPrimaryColumnLinkClick(e, primaryItem);
                    }
                  }}
                  onMouseEnter={() => {
                    if (isConcierge) {
                      onPrimaryColumnButtonClick(primaryIndex);
                    }
                  }}
                  data-analytics-parentCat={primaryItem.displayName}
                  className="p-0 text-inherit"
                >
                  {primaryItem?.displayName?.includes(
                    CATALOG_CATEGORIES.WATERWORKS
                  ) ? (
                    <img
                      src={waterWorksUrl}
                      alt={primaryItem?.displayName}
                      width={188}
                      height={16}
                      className="h-4 w-[188px]"
                    />
                  ) : (
                    <Typography
                      variant="default"
                      fontFamily={
                        selectedPrimaryIndex === primaryIndex
                          ? "rhcRoman"
                          : "rhc"
                      }
                      fontWeight={
                        selectedPrimaryIndex === primaryIndex
                          ? "normal"
                          : "thin"
                      }
                      className={cn(
                        "text-left text-[13px] uppercase leading-[1.5em] lg:text-base",
                        selectedPrimaryIndex === primaryIndex
                          ? "tracking-[0.025em]"
                          : " tracking-[0.05em]"
                      )}
                      dangerouslySetInnerHTML={{
                        __html: he.decode(primaryItem?.displayName ?? "")
                      }}
                    />
                  )}
                </button>
              )
            )}
          </motion.div>

          {/* SECONDARY COLUMN */}
          {selectedPrimaryIndex !== -1 && (
            <motion.div
              key={selectedPrimaryIndex}
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { duration: 0.3 }
              }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              className="columns-2 gap-x-20 overflow-x-auto text-white [-ms-overflow-style:none] [column-fill:_auto] [scrollbar-width:none]"
            >
              {groupedCategories?.map((group, groundIndex) => (
                <div
                  key={groundIndex}
                  className="mb-5 flex break-inside-avoid flex-col items-start gap-2"
                  onClick={e => e.stopPropagation()}
                >
                  {group?.header &&
                    (group?.header?.displayName?.includes(
                      CATALOG_CATEGORIES.WATERWORKS
                    ) ? (
                      <img
                        src={waterWorksUrl}
                        alt={group?.header.displayName ?? ""}
                        width={188}
                        height={16}
                        className="h-4 w-[188px]"
                      />
                    ) : (
                      <Typography
                        variant="default"
                        fontFamily="rhcRoman"
                        fontWeight="normal"
                        className="text-left text-[13px] uppercase leading-[1.5em] tracking-[0.05em] text-inherit lg:text-base"
                        onClick={e => {
                          e.stopPropagation();
                        }}
                        dangerouslySetInnerHTML={{
                          __html: he.decode(group?.header?.displayName ?? "")
                        }}
                      />
                    ))}

                  {group.children.map((secondaryItem, secondaryIndex) => (
                    <button
                      key={secondaryIndex}
                      // data-analytics-parentCat={secondaryItem.displayName}
                      className="p-0 text-inherit"
                      onClick={e => {
                        onSecondaryColumnLinkClick(
                          e,
                          secondaryItem,
                          group?.header
                        );
                      }}
                    >
                      {secondaryItem?.displayName?.includes(
                        CATALOG_CATEGORIES.WATERWORKS
                      ) ? (
                        <img
                          src={waterWorksUrl}
                          alt={secondaryItem?.displayName ?? ""}
                          width={188}
                          height={16}
                          className="h-4 w-[188px]"
                        />
                      ) : (
                        <Typography
                          variant="default"
                          fontFamily="rhc"
                          fontWeight="thin"
                          className="text-left text-[13px] capitalize leading-[1.5em] tracking-[0.2px] will-change-transform lg:text-base"
                          dangerouslySetInnerHTML={{
                            __html: he.decode(secondaryItem?.displayName ?? "")
                          }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default memoize(NavigationDropdown);
